import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from 'ui/components/templates/Layout';
import { IRootState } from '../../../store';
import useOTP from '../../../hooks/useOTP';
import { useRouter } from 'next/router';
import {
  setShowEmailVerificationModal,
  setShowPhoneVerificationModal,
} from '../../../store/auth/actions';
import authService from '../../../services/auth';
import contentService from '../../../services/content';
import dynamic from 'next/dynamic';
import useResize from '../../../hooks/useResize';
import { initFacebookSDK } from '../../atoms/FacebookSDK';
import LoadingAnimation from '../../atoms/LoadingAnimation';

const EmailVerificationModal = dynamic(() => import('../../organism/EmailVerificationModal'), {
  ssr: false,
});

const PhoneVerificationModal = dynamic(() => import('../../organism/PhoneVerificationModal'), {
  ssr: false,
});

const AuthPageTemplate = ({
  children,
  isDac = false,
}: {
  children: React.ReactNode;
  isDac?: boolean;
}) => {
  const router = useRouter();

  const { showPhoneVerificationModal, showEmailVerificationModal, isLoading } = useSelector(
    (state: IRootState) => state.auth
  );
  const dispatch = useDispatch();

  const [banner, setBanner] = useState('');
  const [error, setError] = useState(null);
  const [isInterracted, setisInterracted] = useState(false);

  useResize();

  useEffect(() => {
    contentService.getBackgroundAuth().then((res) => {
      if (res?.data?.bannerUrl) {
        setBanner(res?.data?.bannerUrl);
      }
    });
  }, []);

  useLayoutEffect(() => {
    dispatch(setShowPhoneVerificationModal(false));
    dispatch(setShowEmailVerificationModal(false));
  }, []);

  const {
    error: errorOTP,
    loading: loadingOTP,
    nextValidPhoneNumber,
    generateWAOTP,
  } = useOTP(false, 'verify-phone');

  const handleSubmitVerifyPhone = useCallback(
    async (phoneNumber) => {
      const phone = phoneNumber.startsWith('0') ? phoneNumber : `0${phoneNumber}`;

      try {
        const checkPhoneResponse = await authService.clientCheckPhonenumberVerified({
          phoneNumber: phone,
        });

        if (checkPhoneResponse?.body?.dacData) {
          const dacData = checkPhoneResponse.body.dacData;
          sessionStorage.setItem(
            'dacData',
            JSON.stringify({ ...dacData, phoneInput: phoneNumber })
          );
          switch (dacData.dacType) {
            case 'change_phone_dac':
              return router.replace(
                process.env.NEXT_PUBLIC_URL + `/change-phone-number?temporaryDAC=true`
              );
            case 'register_dac':
              return router.replace(process.env.NEXT_PUBLIC_URL + `/register?temporaryDAC=true`);
            case 'login_dac':
              sessionStorage.setItem('after_login_temporary_dac', 'true');
          }
        }
        sessionStorage.removeItem('dacData');

        const response = await generateWAOTP(phone, true, true);

        if (response) {
          const nextValidAttemptInMs =
            (response?.nextValidAttempt || nextValidPhoneNumber || 300) * 1000;
          const nowInMs = new Date().getTime();

          const nextTime = nowInMs + nextValidAttemptInMs;
          const nextTimeDate = new Date(nextTime);
          nextTimeDate.setSeconds(nextTimeDate.getSeconds() + 1);

          localStorage.setItem('nextValidAttempt', nextTimeDate.toISOString());

          dispatch(setShowPhoneVerificationModal(false));

          sessionStorage.setItem('phone', phone);
          router.push({
            pathname: '/verifikasi-otp',
            query: { source: 'verify-phone' },
          });
        }
      } catch (err) {
        if (
          err?.response?.data?.error?.trim()?.toLowerCase() ===
          'nomor telepon telah digunakan akun lain'
        ) {
          setError(
            'Nomor HP yang Anda gunakan sudah terdaftar sebagai nomor telepon akun lain. Mohon gunakan nomor HP yang berbeda.'
          );
        } else {
          setError('Terjadi masalah silakan coba lagi');
        }
        return;
      }
    },
    [nextValidPhoneNumber, generateWAOTP]
  );

  const handleInteract = () => {
    setisInterracted(true);
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleInteract);
    window.addEventListener('touchstart', handleInteract);

    return () => {
      window.removeEventListener('mousemove', handleInteract);
      window.removeEventListener('touchstart', handleInteract);
    };
  }, []);

  useEffect(() => {
    initFacebookSDK().then(() => {
      // Facebook SDK initialized
      console.log('success init facebook sdk');
    });
  }, []);

  const LoadingOverlay = () => (
    <div className="fixed left-0 top-0 z-[1000] h-full w-full bg-black opacity-50 flex items-center justify-center">
      <LoadingAnimation />
    </div>
  );

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <Layout
        tasiaClassName="bottom-[84px]"
        backToTopClassName="bottom-[84px]"
        withFooter={false}
        navbarProps={{ showBottomMenu: false }}
        showTasia={isInterracted}
      >
        <div id="fb-root" />
        <div className={`md:bg-reliableBlack3 ${isDac ? 'hidden md:block' : 'block'} h-full`}>
          <div className="container xl:px-0 ">
            <div
              role="banner"
              className="hidden fixed left-0 top-[64px] xl:top-[125px] w-[35vw] md:h-[calc(100dvh-64px)] xl:min-h-[calc(100dvh-125px)] overflow-hidden lg:block"
            >
              <img
                src={banner}
                alt="Login background image"
                className="w-full h-full object-cover object-top"
                style={{ opacity: banner ? 1 : 0 }}
              />
            </div>

            <div
              className={`pl-0 flex justify-center items-center md:h-[calc(100dvh-64px)] xl:min-h-[calc(100dvh-125px)] lg:py-4`}
            >
              {/* Form content */}
              {children}
            </div>
          </div>
        </div>
        {isDac && <div className="md:hidden w-full h-full">{children}</div>}

        <PhoneVerificationModal
          showModal={showPhoneVerificationModal}
          handleCloseModal={() => dispatch(setShowPhoneVerificationModal(true))}
          onSubmit={handleSubmitVerifyPhone}
          error={error || errorOTP}
          loading={loadingOTP}
        />

        <EmailVerificationModal
          showModal={showEmailVerificationModal}
          handleCloseModal={() => {
            const referrer = sessionStorage.getItem('refferer');
            sessionStorage.removeItem('refferer');
            router.replace(
              (router?.query?.redirect as string) || referrer || process.env.NEXT_PUBLIC_URL
            );
          }}
        />
      </Layout>
    </>
  );
};

export default AuthPageTemplate;
